import React from 'react';

import { useQuery } from '@tanstack/react-query';
import ContentLoader from 'react-content-loader';
import classNames from 'classnames';

import { useAppDispatch, useAppSelector } from '../../../../../hooks/hooks';
import { getCourseFilters } from '../../../../../requests';
import {
  filterCourses,
  setCourseFilters,
  setActiveFilterType,
} from '../../../../../store/reducers/librarySlice';
import CourseFilterGroup from '../CourseFilterGroup';
import CourseFilterList from '../CourseFilterList';

const CourseFilters: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    secondaryFilters = [],
    stackFilters = [],
    activeFilters,
    activeFilterType,
  } = useAppSelector((state) => state.libraryReducer);

  const { isLoading } = useQuery({
    queryKey: ['courseFilters'],
    queryFn: async () => {
      const filters = await getCourseFilters();
      dispatch(setCourseFilters(filters));
      return filters;
    },
  });

  const handleFilterTypeChange = (type: 'categories' | 'stacks') => {
    dispatch(setActiveFilterType(type));
    dispatch(filterCourses({ secondaryFilter: null, primaryFilter: null }));
  };

  const groupTitle =
    activeFilterType === 'categories' ? 'Categories' : 'Stacks';
  const filterList =
    activeFilterType === 'categories' ? secondaryFilters : stackFilters;

  return (
    <div className="hidden xl:block w-full max-w-[12.25rem] text-regular text-core-dark">
      {isLoading ? (
        <div>
          <div className="mb-2 font-bold">Filter by</div>
          <ContentLoader className="max-w-[12.25rem]">
            <rect x="0" y="0" rx="5" ry="5" width="100%" height="32" />
            <rect x="0" y="40" rx="5" ry="5" width="100%" height="32" />
          </ContentLoader>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2 p-2 font-medium text-regular">
            <div className="font-bold">Filter by</div>
            <div className="flex flex-col gap-2">
              {['categories', 'stacks'].map((type) => (
                <div
                  key={type}
                  className={classNames(
                    'px-2 py-1 rounded cursor-pointer',
                    activeFilterType === type
                      ? 'bg-blue-100 text-blue-primary hover:bg-blue-100'
                      : 'hover:bg-gray-200',
                  )}
                  onClick={() =>
                    handleFilterTypeChange(type as 'categories' | 'stacks')
                  }
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </div>
              ))}
            </div>
          </div>
          <CourseFilterGroup title={groupTitle}>
            <CourseFilterList
              filters={filterList}
              active={activeFilters.secondaryFilter}
              setActive={(secondaryFilter) =>
                dispatch(
                  filterCourses({ secondaryFilter, primaryFilter: null }),
                )
              }
            />
          </CourseFilterGroup>
        </div>
      )}
    </div>
  );
};

export default CourseFilters;
